import React, {
  Component, Fragment, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import algoliasearch from 'algoliasearch/lite';
import {
  InstantSearch,
  SearchBox,
  Hits,
  Stats,
  ClearRefinements,
  useRefinementList,
  usePagination,
  useInstantSearch,
  Configure,
  Pagination,
} from 'react-instantsearch';
import { history } from 'instantsearch.js/es/lib/routers';
import debounce from 'lodash.debounce';
import { analyticsTrack } from '../../../analyticsUtils';
import filterImage from '../../../../assets/images/filter.png';
import filterGreen from '../../../../assets/images/filter-green.svg';
import xDark from '../../../../assets/images/x-dark-2.svg';
import x from '../../../../assets/images/x.svg';
import searchIcon from '../../../../assets/images/search-icon-white.svg';
import loading from '../../../../assets/images/spinner-DNA-2s-200px.svg';
import ExpertiseTagDropdown from '../../common/ExpertiseTagDropdown';
import { isMobile } from '../../../screenUtils';
import FilterItem from './FilterItem';
import EmptyFeaturedProfiles from '../../EmptyFeaturedProfiles';
import RecommendedConsultantCard from '../../RecommendedConsultantCard';
import './AlgoliaInstantSearch.scss';

const filterItems = [
  { title: 'Functional Areas', filterName: 'area_of_expertises.name' },
  { title: 'Role', filterName: 'higher_level_roles.name' },
  { title: 'Therapeutic Areas', filterName: 'therapeutic_areas.name' },
  { title: 'Product Technologies', filterName: 'therapies.name' },
  { title: 'Stages of Development', filterName: 'development_phases', checkbox: true },
  { title: 'Advanced Degree', filterName: 'degrees' },
  { title: 'Seniority', filterName: 'seniority_levels', checkbox: true },
  { title: 'Work Preference', filterName: 'role_types', checkbox: true },
  { title: 'Region', filterName: 'city', checkbox: true },
];

const truncate = (text, length) => (
  text.length > length ? `${text.slice(0, length)}...` : text
);

const processHit = (
  hit,
  csrfToken,
  handleRegister,
  getEmailStatus,
  indexName,
  cloraEmployer,
  projectResourceId,
  location,
  googleOmniauthUrl,
  msOmniauthUrl,
  signInUrl,
  onProjectSharePage,
  inviterId,
) => {
  const { uiState } = useInstantSearch();
  const filters = { ...uiState[indexName].menu, ...uiState[indexName].refinementList };

  return (
    <RecommendedConsultantCard
      consultant={hit}
      location={location}
      csrfToken={csrfToken}
      idx={hit.position}
      handleRegister={handleRegister}
      getEmailStatus={getEmailStatus}
      filters={filters}
      cloraEmployer={cloraEmployer}
      projectResourceId={projectResourceId}
      googleOmniauthUrl={googleOmniauthUrl}
      msOmniauthUrl={msOmniauthUrl}
      signInUrl={signInUrl}
      onProjectSharePage={onProjectSharePage}
      inviterId={inviterId}
    />
  );
};

const transformItems = (items) => (
  items.map((item, index) => ({
    ...item,
    position: index,
  })));

function LoadingIndicator() {
  const { status } = useInstantSearch();

  if (status === 'loading' || status === 'stalled') {
    return (
      <div className="loading">
        <img src={loading} alt="Loading_image" />
      </div>
    );
  }
  return null;
}

function SearchPagination(props) {
  const { userData } = props;
  const { currentRefinement, pages } = usePagination(props);

  /* Using setTimeout to scroll to the top,
    as the Pagination component doesn't have any function we can use for that.
    Additionally, results are returned in milliseconds, which is why we set it to 1 second.
  */
  setTimeout(() => { window.scrollTo({ top: 0 }); }, 1000);

  return [
    <div className="centered-pagination">
      <Pagination
        className="pagination"
        showFirst={false}
        showLast={false}
        padding={isMobile() ? 1 : 3}
        translations={{
          previousPageItemText: '< Prev',
          nextPageItemText: 'Next >',
        }}
      />
    </div>,
    (currentRefinement === pages.length - 1)
      ? <EmptyFeaturedProfiles userData={userData} /> : '',
  ];
}

function SearchErrorRefresh() {
  const { addMiddlewares } = useInstantSearch();
  const [error, setError] = useState(null);

  useEffect(() => {
    const middleware = ({ instantSearchInstance }) => {
      function handleError(searchError) {
        setError(searchError);
      }

      return {
        subscribe() {
          instantSearchInstance.addListener('error', handleError);
        },
        unsubscribe() {
          instantSearchInstance.removeListener('error', handleError);
        },
      };
    };

    return addMiddlewares(middleware);
  }, [addMiddlewares]);

  if (!error) {
    return null;
  }

  window.location.reload();
}

function CustomCheckboxList(props) {
  const { attribute } = props;
  const { items, refine } = useRefinementList(props);
  let transformedItems = [...items];
  const attributeMappings = {
    seniority_levels: {
      Associate: 1,
      Manager: 2,
      Director: 3,
      'Vice President': 4,
      'C-Level': 5,
    },
    development_phases: {
      Discovery: 1,
      'Pre-clinical': 2,
      'I/Exploratory': 3,
      'II/Proof of Concept': 4,
      'III/Pivotal': 5,
      'IV/Post-approval': 6,
      'Approved Product': 7,
      'CE Mark': 8,
    },
  };

  if (attribute in attributeMappings) {
    const attributeMapping = attributeMappings[attribute];
    transformedItems = transformedItems
      .filter((item) => Object.prototype.hasOwnProperty.call(attributeMapping, item.label))
      .map((item) => ({ ...item, sortValue: attributeMapping[item.label] }))
      .sort((a, b) => a.sortValue - b.sortValue);
  }

  return (
    <ul className="query-refinement">
      {transformedItems.map((item) => (
        <li key={item.label}>
          <label className="custom-checkbox-container" htmlFor={item.value}>
            <input
              id={item.value}
              name={item.value}
              type="checkbox"
              className="checkbox-box"
              checked={item.isRefined}
              onChange={() => refine(item.value)}
            />
            <span className="checkmark" />
            <span className={`checkbox-text ${item.isRefined ? 'active' : ''}`}>
              {truncate(item.label, 30)}
            </span>
          </label>
        </li>
      ))}
    </ul>
  );
}

CustomCheckboxList.propTypes = {
  attribute: PropTypes.string.isRequired,
};

const filterPicklist = (picklist, input) => picklist.filter(
  (record) => record.label.toLowerCase().includes(input.toLowerCase()),
);

function SelectedRefinementTags(props) {
  const { imageStyle } = props;
  const { items, refine } = useRefinementList(props);

  return (items.filter((i) => i.isRefined).map((item) => (
    <button className="selected-tag" onClick={() => refine(item.value)}>
      <div className="text">{ item.label }</div>
      <img
        src={imageStyle === 'green' ? x : xDark}
        className="remove-tag"
        alt={`remove ${item.label}`}
      />
    </button>
  )));
}

function MobileShowConsultantsButton(props) {
  const { toggleRefinePanel } = props;
  const { results } = useInstantSearch();

  return (
    <button className="mobile-show-consultants" onClick={toggleRefinePanel}>
      {`Show ${results.nbHits} Consultants`}
    </button>
  );
}

MobileShowConsultantsButton.propTypes = {
  toggleRefinePanel: PropTypes.func,
};

const filteredCount = () => {
  const { indexUiState } = useInstantSearch();

  return indexUiState.refinementList
    ? filterItems.reduce((aggr, item) => (
      ((indexUiState.refinementList[item.filterName]
        && indexUiState.refinementList[item.filterName].length) || 0) + aggr
    ), 0) : 0;
};

function ClearSelection(props) {
  const { className } = props;

  return (filteredCount() > 0
    && (
    <ClearRefinements
      className={className || 'clear-query'}
      translations={{
        resetButtonText: 'Clear All',
      }}
    />
    )
  );
}

function MobileRefinementStatus(props) {
  const { toggleRefinePanel, showMenu } = props;

  return [
    <button
      className={`toggle-refine-panel ${showMenu ? 'd-none' : ''}`}
      onClick={toggleRefinePanel}
    >
      <img src={filterGreen} className="me-3" alt="funnel" />
      Refine Results
      {filteredCount > 0 ? ` (${filteredCount})` : ''}
    </button>,
    <div className={`mobile-selected-tags ${showMenu ? 'd-none' : ''}`}>
      { filterItems.map((item) => (
        <SelectedRefinementTags
          imageStyle="white"
          attribute={item.filterName}
        />
      ))}
      <ClearSelection />
    </div>,
  ];
}

function SelectionSubtext(props) {
  return filteredCount() > 0
  && (
  <div className="selection-subtext">
    Your keyword recommendations based on your project details.
    Tip: For more results, update
    <a href={`/projects/${props.projectResourceId}/edit`}> Project Details </a>
    or try different filters.
  </div>
  );
}

function CustomMenu(props) {
  const { model, title, handleSelect } = props;
  const { items, refine, canRefine } = useRefinementList(props);
  let transformedItems = [...items];

  if (model === 'degrees') {
    const validDegrees = [
      'MD (Doctor of Medicine)',
      'MPH (Master of Public Health)',
      'MBA (Master of Business Administration)',
      'PharmD (Doctor of Pharmacy)',
      'PhD (Doctor of Philosophy)'];
    transformedItems = transformedItems.filter((item) => validDegrees.includes(item.label));
  }

  return (
    <ExpertiseTagDropdown
      model={model}
      selection={transformedItems.filter((i) => i.isRefined).map((item) => (
        { label: truncate(item.label, 25), value: item.value }
      ))}
      handleSelect={() => handleSelect(refine)}
      handleRemove={(value) => refine(value)}
      heading={title}
      menuHeading={`Select ${title}`}
      isDisabled={!canRefine}
      picklist={transformedItems}
      loadOptions={(input) => (
        new Promise((resolve) => { resolve(filterPicklist(transformedItems, input)); }))}
      showSubHeading={false}
      showMenuFooter={false}
      ctaVerb="Select "
      bodyClass=""
      plusColor="alt"
      multi
    />
  );
}
CustomMenu.propTypes = {
  model: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  handleSelect: PropTypes.func.isRequired,
};

const transformFilters = (state, format = 'url') => {
  const transformedFilters = {};
  const filters = {
    aoes: 'area_of_expertises.name',
    tas: 'therapeutic_areas.name',
    therapies: 'therapies.name',
    development_phases: 'development_phases',
    roles: 'seniority_levels',
    regions: 'city',
    educations: 'degrees',
    hlrs: 'higher_level_roles.name',
    work_preference: 'role_types',
  };

  Object.entries(filters).forEach(([key, value]) => {
    const transformedKey = format === 'url' ? value : key;
    if (Object.prototype.hasOwnProperty.call(state, transformedKey)) {
      const stateKey = format === 'url' ? key : value;
      transformedFilters[stateKey] = state[transformedKey];
    }
  });

  return transformedFilters;
};

const routing = (indexName) => ({
  router: history(),
  stateMapping: {
    stateToRoute(uiState) {
      const indexUiState = uiState[indexName];
      let urlFilters = {};

      if (indexUiState && indexUiState.refinementList) {
        urlFilters = transformFilters(indexUiState.refinementList);
      }

      return {
        q: indexUiState.query,
        page: indexUiState.page,
        ...urlFilters,
      };
    },
    routeToState(routeState) {
      let algoliaFilters = {};
      if (Object.keys(routeState).length > 0) {
        algoliaFilters = transformFilters(routeState, 'algolia');
      }

      return {
        [indexName]: {
          query: routeState.q,
          page: routeState.page,
          refinementList: algoliaFilters,
        },
      };
    },
  },
});

const renderStats = (className) => (
  <div className={`stats-container  mb-3  ${className}`}>
    <Stats
      translations={{
        rootElementText({ nbHits }) {
          return `${nbHits} consultants you can connect with`;
        },
      }}
    />
  </div>
);

const reportSearchToSegment = debounce((filters, query, numResults, location) => {
  const filtersLen = Object.values(filters).reduce((aggr, val) => (aggr + val.length), 0);

  if (filtersLen > 0 || (query && query.length)) {
    analyticsTrack('performedSearch', {
      location,
      properties: { facets: filters, queryString: query, numResults },
    });
  }
}, 1000);

function SegmentReporter(props) {
  const { results, indexUiState } = useInstantSearch();
  const { location } = props;
  const filters = { ...indexUiState.menu, ...indexUiState.refinementList };
  const query = indexUiState && indexUiState.query;

  reportSearchToSegment(filters, query, results.nbHits, location);
  return null;
}

const hitsPerPage = (onProjectSharePage) => {
  if (onProjectSharePage) return 1;

  return isMobile() ? 5 : 20;
};

class AlgoliaInstantSearch extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showMenu: !isMobile() || props.onProjectSharePage,
    };

    this.handleSelect = this.handleSelect.bind(this);
    this.toggleRefinePanel = this.toggleRefinePanel.bind(this);
  }

  toggleRefinePanel() {
    const { showMenu } = this.state;

    this.setState({ showMenu: !showMenu });
  }

  handleSelect(refine) {
    return (tags) => (
      tags.length
        ? tags.find((tag) => {
          if (tag.isRefined === false) { refine(tag.value); return true; }
          return false;
        }) : refine()
    );
  }

  renderFilterItems() {
    return (filterItems.map((item) => (
      item.checkbox
        ? (
          <FilterItem filterTitle={item.title} key={item.filterName}>
            <CustomCheckboxList
              attribute={item.filterName}
              sortBy={['name']}
            />
          </FilterItem>
        )
        : (
          <CustomMenu
            key={item.filterName}
            model={item.filterName}
            attribute={item.filterName}
            title={item.title}
            filterName={item.filterName}
            handleSelect={this.handleSelect}
            limit={100}
            sortBy={['name']}
          />
        )
    )));
  }

  render() {
    const {
      indexName,
      agAppID,
      publicKey,
      csrfToken,
      handleRegister,
      getEmailStatus,
      userData,
      cloraEmployer,
      projectResourceId,
      initialFilters,
      location,
      googleOmniauthUrl,
      msOmniauthUrl,
      signInUrl,
      onProjectSharePage,
      inviterId,
    } = this.props;
    const { showMenu } = this.state;
    return (
      <InstantSearch
        indexName={indexName}
        searchClient={algoliasearch(agAppID, publicKey)}
        routing={initialFilters ? null : routing(indexName)}
        initialUiState={{ [indexName]: initialFilters }}
      >
        <Configure hitsPerPage={hitsPerPage(onProjectSharePage)} />
        <div className="instant-search-container">
          <SegmentReporter location={location} />
          {onProjectSharePage
            ? (
              <Fragment>
                <LoadingIndicator />
                <Hits
                  hitComponent={({ hit }) => processHit(
                    hit,
                    csrfToken,
                    handleRegister,
                    getEmailStatus,
                    indexName,
                    cloraEmployer,
                    projectResourceId,
                    location,
                    googleOmniauthUrl,
                    msOmniauthUrl,
                    signInUrl,
                    onProjectSharePage,
                  )}
                  transformItems={transformItems}
                  classNames={{
                    list: 'rec-card-container',
                  }}
                />
              </Fragment>
            )
            : (
              <Fragment>
                <div className="search-header">
                  <div className="search-header-text">
                    Find Top Consultants
                  </div>
                  <div className="project-recommended-tab-stats">
                    <div className="recommended-consultants">
                      Recommended Consultants
                    </div>
                    <div className="stats">
                      { renderStats('stats-left') }
                    </div>
                  </div>
                  <div className="search-input">
                    <SearchBox
                      placeholder="Search Ex: Clinical Operations Consultants"
                      submitIconComponent={() => (
                        <img src={searchIcon} alt="magnifying glass" />
                      )}
                    />
                  </div>
                </div>
                <div className="search-result-container">
                  <MobileRefinementStatus
                    toggleRefinePanel={this.toggleRefinePanel}
                    showMenu={showMenu}
                  />
                  <div className={`left-panel ${showMenu ? '' : 'd-none'}`}>
                    <div className="left-panel-header">
                      Refine Results
                      <img alt="funnel" src={filterImage} className="ms-2 filter-image" />
                      <button onClick={this.toggleRefinePanel} className="close-refine-panel">
                        <img alt="Close Panel" src={xDark} />
                      </button>
                    </div>
                    { this.renderFilterItems() }
                    <MobileShowConsultantsButton toggleRefinePanel={this.toggleRefinePanel} />
                    <ClearSelection />
                  </div>
                  {renderStats('stats-left')}
                  <div className="right-panel">
                    <LoadingIndicator />
                    <div className="selected-tags">
                      { filterItems.map((item) => (
                        <SelectedRefinementTags
                          imageStyle="green"
                          attribute={item.filterName}
                        />
                      ))}
                      <ClearSelection />
                      { projectResourceId
                    && (
                    <SelectionSubtext
                      projectResourceId={projectResourceId}
                    />
                    )}
                    </div>
                    {renderStats('stats-right')}
                    <Hits
                      hitComponent={({ hit }) => processHit(
                        hit,
                        csrfToken,
                        handleRegister,
                        getEmailStatus,
                        indexName,
                        cloraEmployer,
                        projectResourceId,
                        location,
                        googleOmniauthUrl,
                        msOmniauthUrl,
                        signInUrl,
                        inviterId,
                      )}
                      transformItems={transformItems}
                      classNames={{
                        list: 'rec-card-container',
                      }}
                    />
                    <SearchErrorRefresh />
                    <SearchPagination userData={userData} />
                  </div>
                </div>
              </Fragment>
            )}
        </div>
      </InstantSearch>
    );
  }
}

AlgoliaInstantSearch.propTypes = {
  indexName: PropTypes.string.isRequired,
  agAppID: PropTypes.string.isRequired,
  publicKey: PropTypes.string.isRequired,
  handleRegister: PropTypes.func,
  getEmailStatus: PropTypes.func,
  userData: PropTypes.shape({}),
  csrfToken: PropTypes.string.isRequired,
  cloraEmployer: PropTypes.bool,
  initialFilters: PropTypes.shape({}),
  projectResourceId: PropTypes.string,
  location: PropTypes.string,
  googleOmniauthUrl: PropTypes.string,
  msOmniauthUrl: PropTypes.string,
  signInUrl: PropTypes.string,
  onProjectSharePage: PropTypes.bool,
  inviterId: PropTypes.string,
};

AlgoliaInstantSearch.defaultProps = {
  handleRegister: () => {},
  getEmailStatus: () => null,
  cloraEmployer: false,
  initialFilters: null,
  projectResourceId: null,
  location: 'Featured Page',
  onProjectSharePage: false,
  inviterId: null,
  googleOmniauthUrl: null,
  msOmniauthUrl: null,
  signInUrl: null,
};

export default AlgoliaInstantSearch;
